import { useState } from "react"
import { DateRangePicker } from "rsuite"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
} from "@mui/material"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import Chart from "@/components/charts/Chart"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { BrandSelect } from "../../Brand/BrandSelect"
import { useAppSelector } from "@/lib/hooks"

const WeeklyReport = () => {
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 42)), // 6 weeks ago
    new Date(), // Today
  ])

  const selectedBrand = useAppSelector((state) => state.brand.selectedBrandId)

  const { data, isLoading } = useCustomQuery(
    ["weekly-sales-report", dateRange[0], dateRange[1], selectedBrand],
    `report/sales/weekly?start_date=${dateRange[0].toISOString()}&end_date=${dateRange[1].toISOString()}&brand_id=${selectedBrand}`
  )

  const reversedData = data ? [...data].reverse() : []

  // Prepare chart data
  const chartData = {
    labels: [],
    salesData: [],
    ordersData: [],
    detailedLabels: [], // For tooltips
  }

  if (data) {
    data.forEach((item) => {
      chartData.labels.push(item.week_label)
      chartData.salesData.push(item.total_sales)
      chartData.ordersData.push(item.total_orders)
      // Extract the week number from week_label (e.g., "Week-12")
      const weekNumber = item.week_label.split("-")[1]
      chartData.detailedLabels.push(`Week ${weekNumber}`)
    })
  }

  const handleExport = async () => {
    return exportReport({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      reportType: "weekly-sales",
    })
  }

  // Calculate summary stats
  const summaryStats = data
    ? {
        totalSales: data.reduce(
          (sum, item) => sum + parseFloat(item.total_sales),
          0
        ),
        totalOrders: data.reduce(
          (sum, item) => sum + parseInt(item.total_orders),
          0
        ),
        averageSales:
          data.reduce((sum, item) => sum + parseFloat(item.total_sales), 0) /
          data.length,
        averageOrders:
          data.reduce((sum, item) => sum + parseInt(item.total_orders), 0) /
          data.length,
        bestWeek: data.reduce(
          (max, item) =>
            parseFloat(item.total_sales) > parseFloat(max.total_sales)
              ? item
              : max,
          data[0] || null
        ),
      }
    : null

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Weekly Sales Report
      </Typography>

      {/* Date Range Picker Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Date Range
          </Typography>
          <div className="flex justify-between gap-2">
            <DateRangePicker
              className="flex-1"
              value={dateRange}
              onChange={setDateRange}
              format="MMM dd, yyyy"
              placeholder="Select date range"
              cleanable={false}
              style={{ width: 300 }}
              appearance="default"
              hoverRange="week"
              defaultCalendarValue={[
                new Date(new Date().setDate(new Date().getDate() - 42)), // 6 weeks ago
                new Date(),
              ]}
            />
            <div className="flex-1">
              <BrandSelect className="w-full" />
            </div>
            <Button
              variant="outlined"
              onClick={() => handleExport()}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summaryStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Sales</Typography>
              <Typography variant="h5" color="primary">
                Rs.{" "}
                {summaryStats.totalSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Orders</Typography>
              <Typography variant="h5" color="secondary">
                {summaryStats.totalOrders.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Avg. Weekly Sales</Typography>
              <Typography variant="h5">
                Rs.{" "}
                {summaryStats.averageSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Best Week</Typography>
              <Typography>
                {summaryStats.bestWeek?.week_label} (Rs.{" "}
                {parseFloat(
                  summaryStats.bestWeek?.total_sales || 0
                ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                )
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Sales
              </Typography>
              <Chart
                type="line"
                data={chartData.salesData}
                labels={chartData.detailedLabels}
                title="Weekly Sales"
                backgroundColor="rgba(63, 81, 181, 0.5)"
                borderColor="#3f51b5"
                tooltipLabel="Sales"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Orders
              </Typography>
              <Chart
                type="bar"
                data={chartData.ordersData}
                labels={chartData.detailedLabels}
                title="Weekly Orders"
                backgroundColor="rgba(76, 175, 80, 0.5)"
                borderColor="#4caf50"
                tooltipLabel="Orders"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Weekly Data
          </Typography>
          <DataTable
            columns={columns}
            data={reversedData}
            isLoading={isLoading}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default WeeklyReport
